import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Stack,
  Snackbar,
  Alert,
  Avatar,
  useTheme,
  Fade,
  Zoom,
  alpha
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { COR_PRINCIPAL_DEFAULT, DUPR_STATUS_CONCLUIDO } from '../../constants';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import Footer from '../../Components/Menu/Footer';
import apiContaReceber from '../../api/apiContaReceber';
import { formatarDinheiro, getIsSmallScreen } from '../../helper';
import { DadosLinkPagamento } from '../../types/ContaReceberTypes';
import LinkPagamentoLoading from './LinkPagamentoLoading';
import LinkPagamentoPago from './LinkPagamentoPago';
import LinkPagamentoErro from './LinkPagamentoErro';
import LinkPagamentoAguardandoConciliacao from './LinkPagamentoAguardandoConciliacao';
import TabelaDebitosLinkPagamento from './TabelaDebitosLinkPagamento';
import QRCode from 'qrcode.react';
import LogoEmpresa from '../../Components/generic/LogoEmpresa';
import PixExpiracaoBar from '../../Components/generic/PixExpiracaoBar';
import { AxiosError } from 'axios';

const LinkPagamentoPage: React.FC = () => {
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = getIsSmallScreen();
  const [openToast, setOpenToast] = useState(false);
  const [copying, setCopying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [corPrincipal, setCorPrincipal] = useState(COR_PRINCIPAL_DEFAULT);
  const [dados, setDados] = useState<DadosLinkPagamento | null>(null);
  const [jaPago, setJaPago] = useState(false);
  const [erro, setErro] = useState(false);
  const [aguardandoConciliacao, setAguardandoConciliacao] = useState(false);
  const [expirado, setExpirado] = useState(false);

  const handleCopy = async (text: string) => {
    try {
      setCopying(true);

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setOpenToast(true);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    } finally {
      setTimeout(() => setCopying(false), 500);
    }
  };

  useEffect(() => {
    getDadosLinkPagamento();
  }, [id]);

  const getDadosLinkPagamento = async () => {
    if (!id?.trim()?.length) return;

    try {
      setLoading(true);
      // Nova consulta pode ter desfeito o estado anterior (ex.: conciliação confirmada
      // entre duas chamadas); zera antes de aplicar a resposta para não prender a tela.
      setAguardandoConciliacao(false);
      setJaPago(false);
      setErro(false);

      const res = await apiContaReceber.getDadosLinkPagamento(id);
      setDados(res);
      setExpirado(false);

      if (res?.corPrincipal) {
        setCorPrincipal(res.corPrincipal);
      }

      if (res?.status === DUPR_STATUS_CONCLUIDO) {
        setJaPago(true);
      }

      if (res?.aguardandoConciliacao) {
        setAguardandoConciliacao(true);
        return;
      }

      if (!res?.pixCode?.trim()?.length) {
        console.error('Qr code do pix não foi gerado.');
        setErro(true);
      }
    } catch (error: unknown) {
      if (
        error instanceof AxiosError &&
        typeof error.response?.data === 'string' &&
        error.response.data.includes('Este PIX já foi pago')
      ) {
        setJaPago(true);
        return;
      }

      setErro(true);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const gerarNovoPix = () => {
    getDadosLinkPagamento();
  };

  if (loading) {
    return <LinkPagamentoLoading corPrincipal={corPrincipal} theme={theme} />;
  }

  if (jaPago) {
    return <LinkPagamentoPago />;
  }

  if (aguardandoConciliacao) {
    return <LinkPagamentoAguardandoConciliacao />;
  }

  if (erro) {
    return <LinkPagamentoErro />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(145deg, ${alpha(corPrincipal, 0.05)} 0%, ${alpha(corPrincipal, 0.02)} 100%)`,
        py: { xs: 3, md: 6 },
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '300px',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          clipPath: 'polygon(0 0, 100% 0, 100% 60%, 0 100%)',
          zIndex: 0
        }
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <Grid
          container
          spacing={{ xs: 2, md: 4 }}
          sx={{
            mb: 4,
            display: 'flex',
            '& > .MuiGrid-item': {
              display: 'flex'
            }
          }}
        >
          <Grid item xs={12} lg={8} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Stack spacing={3} sx={{ height: '100%' }}>
              <Fade in={true} timeout={600}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 3, md: 4 },
                      background: `linear-gradient(105deg, ${alpha(corPrincipal, 0.15)} 0%, ${alpha(corPrincipal, 0.05)} 100%)`,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                      flexShrink: 0
                    }}
                  >
                    <Box>
                      <LogoEmpresa />
                    </Box>
                    <Box>
                      <Typography
                        variant="overline"
                        fontWeight="bold"
                        color="text.secondary"
                        sx={{ letterSpacing: 1.5 }}
                      >
                        Valor a pagar
                      </Typography>
                      <Typography
                        variant="h3"
                        fontWeight="800"
                        sx={{
                          fontSize: { xs: '2rem', md: '2.5rem' },
                          background: corPrincipal,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          lineHeight: 1.2
                        }}
                      >
                        R$ {formatarDinheiro(dados?.valorCorrigido || 0)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        textAlign: 'right',
                        p: 2,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                        backdropFilter: 'blur(4px)'
                      }}
                    >
                      <Typography
                        variant="overline"
                        fontWeight="bold"
                        color="text.secondary"
                        sx={{ letterSpacing: 1.5 }}
                      >
                        Vencimento
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarTodayIcon sx={{ color: corPrincipal, fontSize: 20 }} />
                        <Typography variant="h6" fontWeight="bold" style={{ color: corPrincipal }}>
                          {dados?.vencimento || ''}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: { xs: 2, md: 4 },
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    {expirado ? (
                      <Box sx={{ textAlign: 'center', width: '100%' }}>
                        <Alert severity="warning" sx={{ mb: '16px', textAlign: 'left' }}>
                          O QR Code PIX expirou. Gere um novo código para continuar o pagamento.
                        </Alert>
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<RefreshIcon />}
                          onClick={gerarNovoPix}
                          sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            background: corPrincipal,
                            boxShadow: `0 8px 16px -4px ${alpha(theme.palette.primary.main, 0.3)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': { background: corPrincipal }
                          }}
                        >
                          Gerar novo QR Code
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <Grid container spacing={5} alignItems="center">
                          <Grid item xs={12} md={5} display="flex" justifyContent="center">
                            <Zoom in={true} timeout={600}>
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: 'white',
                                  borderRadius: 4,
                                  boxShadow: `0 15px 30px -10px ${alpha(theme.palette.primary.main, 0.2)}`,
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                  transition: 'transform 0.3s ease',
                                  '&:hover': { transform: 'scale(1.02)' }
                                }}
                              >
                                <QRCode
                                  value={dados?.pixCode || ''}
                                  size={isMobile ? 160 : 240}
                                  elevation={0}
                                />
                              </Box>
                            </Zoom>
                          </Grid>
                          <Grid item xs={12} md={7}>
                            <Typography
                              variant="h5"
                              fontWeight="bold"
                              gutterBottom
                              sx={{
                                background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.dark} 100%)`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                              }}
                            >
                              Pague via PIX
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 3,
                                p: 2,
                                bgcolor: alpha(corPrincipal, 0.1),
                                borderRadius: 2,
                                borderLeft: `4px solid ${corPrincipal}`,
                                lineHeight: 1.8
                              }}
                            >
                              • Abra o app do seu banco
                              <br />
                              • Escolha &quot;Pagar com QR Code&quot; ou &quot;Pix Copia e
                              Cola&quot;
                              <br />• Finalize o pagamento e a liberação é imediata
                            </Typography>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={<ContentCopyIcon />}
                              onClick={() => handleCopy(dados?.pixCode || '')}
                              disabled={copying}
                              fullWidth
                              sx={{
                                borderRadius: 3,
                                textTransform: 'none',
                                background: corPrincipal,
                                boxShadow: `0 8px 16px -4px ${alpha(theme.palette.primary.main, 0.3)}`,
                                transition: 'all 0.3s ease',
                                '&:hover': { background: corPrincipal }
                              }}
                            >
                              {'Copiar Código PIX'}
                            </Button>
                          </Grid>
                        </Grid>
                        <Box sx={{ mt: '24px', width: '100%' }}>
                          <PixExpiracaoBar
                            dataExpiracao={dados?.pixExpiracao}
                            tempoTotalSegundos={dados?.pixTempoExpiracao}
                            onExpirar={() => setExpirado(true)}
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                </Paper>
              </Fade>

              <Fade in={true} timeout={800} style={{ flex: 1 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: alpha(corPrincipal, 0.1),
                        color: corPrincipal
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      Detalhes da Cobrança
                    </Typography>
                  </Stack>
                  <Grid container spacing={2} sx={{ flex: 1 }}>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.background.default, 0.6),
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          height: '100%'
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="text.disabled"
                          display="block"
                          gutterBottom
                          sx={{ letterSpacing: 1, fontSize: '0.6rem' }}
                        >
                          FATURA
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {dados?.codigoFatura || '197683'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.background.default, 0.6),
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          height: '100%'
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="text.disabled"
                          display="block"
                          gutterBottom
                          sx={{ letterSpacing: 1, fontSize: '0.6rem' }}
                        >
                          VALOR ORIGINAL
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          R$ {formatarDinheiro(dados?.valorOriginal || 99.9)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.background.default, 0.6),
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          height: '100%'
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="text.disabled"
                          display="block"
                          gutterBottom
                          sx={{ letterSpacing: 1, fontSize: '0.6rem' }}
                        >
                          JUROS
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          R$ {formatarDinheiro(dados?.valorJuros || 0)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Fade>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
            <Stack spacing={3} sx={{ width: '100%', height: '100%' }}>
              <Fade in={true} timeout={700} style={{ flex: 1 }}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(corPrincipal, 0.1)}`,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                  elevation={0}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: alpha(corPrincipal, 0.1),
                        color: corPrincipal
                      }}
                    >
                      <AccountCircleIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      Dados do Cliente
                    </Typography>
                  </Stack>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(corPrincipal, 0.05),
                        borderRadius: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="text.disabled"
                        sx={{ letterSpacing: 1 }}
                      >
                        TITULAR
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{ textTransform: 'uppercase' }}
                      >
                        {dados?.nomeTitular || ''}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                        borderRadius: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="text.disabled"
                        sx={{ letterSpacing: 1 }}
                      >
                        DOCUMENTO
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {dados?.documentoTitular || ''}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Fade>

              <Fade in={true} timeout={800} style={{ flex: 1 }}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                  elevation={0}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: alpha(corPrincipal, 0.1),
                        color: corPrincipal
                      }}
                    >
                      <BusinessIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      Emissor
                    </Typography>
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        px: 2,
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                        borderRadius: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="text.disabled"
                        sx={{ letterSpacing: 1 }}
                      >
                        RAZÃO SOCIAL
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {dados?.razaoSocialEmissor || ''}
                      </Typography>
                      <Typography variant="caption">{dados?.nomeFantasiaEmissor || ''}</Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 2,
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                        borderRadius: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="text.disabled"
                        sx={{ letterSpacing: 1 }}
                      >
                        CNPJ
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {dados?.documentoEmissor || ''}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 2,
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                        borderRadius: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="text.disabled"
                        sx={{ letterSpacing: 1 }}
                      >
                        CONTATO
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {dados?.contatoEmissor || ''}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Fade>
            </Stack>
          </Grid>
        </Grid>

        <TabelaDebitosLinkPagamento
          debitosAbertos={dados?.debitosAbertos || []}
          codigoFatura={dados?.codigoFatura || ''}
          corPrincipal={corPrincipal}
          isMobile={isMobile}
        />

        <Box
          sx={{
            mt: { xs: 2, md: 3 },
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            width: '100%'
          }}
        >
          <Footer backgroundColor="transparent" />
        </Box>
      </Container>

      <Snackbar
        open={openToast}
        autoHideDuration={3000}
        onClose={() => setOpenToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setOpenToast(false)}
          severity="success"
          icon={<CheckCircleIcon fontSize="inherit" />}
          sx={{
            width: '100%',
            borderRadius: 3,
            boxShadow: `0 10px 20px -5px ${alpha(theme.palette.success.main, 0.3)}`,
            fontWeight: 'bold'
          }}
        >
          Código copiado com sucesso!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LinkPagamentoPage;
